import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { 
  LayoutDashboard, 
  Calendar, 
  Users, 
  Target, 
  Trophy, 
  DollarSign,
  Menu,
  X,
  LogOut,
  Building2,
  UsersRound,
  Crosshair,
  UserCog,
  Megaphone,
  FileText,
  Search,
  ClipboardList,
  History,
  Settings,
  BarChart3,
  KeyRound,
  Monitor
} from "lucide-react";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { PushNotificationButton } from "@/components/PushNotifications";

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout, isAdmin } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Navigation items organized by category
  const navItems = [
    // Main Navigation
    { to: "/", icon: LayoutDashboard, label: "Dashboard", adminOnly: false },
    { to: "/today", icon: Calendar, label: "Today's Events", adminOnly: false },
    { to: "/events", icon: Calendar, label: "All Events", adminOnly: false },
    
    // Competition & Results
    { to: "/leaderboard", icon: Trophy, label: "Leaderboard", adminOnly: false },
    { to: "/shootoffs", icon: Crosshair, label: "Shoot-Offs", adminOnly: false },
    { to: "/my-results", icon: ClipboardList, label: "My Results", adminOnly: false },
    { to: "/search", icon: Search, label: "Search", adminOnly: false },
    
    // People
    { to: "/shooters", icon: Users, label: "Shooters", adminOnly: false },
    { to: "/announcements", icon: Megaphone, label: "Announcements", adminOnly: false },
    
    // Admin - Scoring
    { to: "/score-entry", icon: Target, label: "Score Entry", adminOnly: true },
    { to: "/score-history", icon: History, label: "Score History", adminOnly: true },
    { to: "/match-results", icon: BarChart3, label: "Match Results", adminOnly: true },
    
    // Admin - Business
    { to: "/payments", icon: DollarSign, label: "Payments", adminOnly: true },
    { to: "/sponsors", icon: Building2, label: "Sponsors", adminOnly: true },
    { to: "/reports", icon: FileText, label: "Reports", adminOnly: true },
    
    // Admin - Management
    { to: "/users", icon: UserCog, label: "Users", adminOnly: true },
    { to: "/settings", icon: Settings, label: "Settings", adminOnly: true },
    { to: "/system", icon: Monitor, label: "System", adminOnly: true },
  ];

  const filteredNavItems = navItems.filter(item => {
    if (item.adminOnly && !isAdmin()) return false;
    if (item.requiresAuth && !user) return false;
    return true;
  });

  return (
    <div className="flex min-h-screen">
      {/* Mobile menu button */}
      <button
        data-testid="mobile-menu-btn"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 text-white rounded-lg shadow-lg"
        style={{ backgroundColor: settings.sidebar_color || '#1B3B6F' }}
      >
        {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-40
          w-64 transform transition-transform duration-300
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        `}
        style={{ backgroundColor: settings.sidebar_color || '#1B3B6F', minHeight: '100vh' }}
      >
        <div className="p-6">
          {settings.logo_url && (
            <img 
              src={settings.logo_url} 
              alt={settings.organization_name || 'Logo'} 
              className="h-16 w-auto mb-3 object-contain"
            />
          )}
          <h1 className="text-white font-serif text-2xl font-bold tracking-tight" data-testid="app-title">
            {settings.organization_name || 'NMLRA'}
          </h1>
          <p className="text-white/60 text-sm mt-1">Trap Tracker</p>
        </div>

        <nav className="px-4 space-y-1 pb-48 overflow-y-auto max-h-[calc(100vh-180px)]">
          {filteredNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-').replace('-', '')}`}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `sidebar-link ${isActive ? "active" : ""}`
              }
            >
              <item.icon size={20} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User info and logout */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          {user ? (
            <div className="space-y-3">
              <div className="text-white/80 text-sm">
                <p className="font-medium">{user.name}</p>
                <p className="text-white/50 text-xs">{user.email}</p>
                <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs ${
                  user.role === "admin" 
                    ? "bg-amber-500/20 text-amber-300" 
                    : "bg-white/10 text-white/60"
                }`}>
                  {user.role}
                </span>
              </div>
              <PushNotificationButton className="w-full text-xs" />
              <NavLink
                to="/account-settings"
                onClick={() => setSidebarOpen(false)}
                className="flex items-center justify-center gap-2 w-full py-2 text-sm text-white/70 hover:text-white hover:bg-white/10 rounded-md transition-colors"
                data-testid="account-settings-link"
              >
                <KeyRound size={16} />
                Account Settings
              </NavLink>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="w-full text-white/70 hover:text-white hover:bg-white/10"
                data-testid="logout-btn"
              >
                <LogOut size={16} className="mr-2" />
                Logout
              </Button>
            </div>
          ) : (
            <div className="text-white/40 text-xs text-center">
              National Muzzle Loading
              <br />
              Rifle Association
            </div>
          )}
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 main-content p-4 lg:p-8 lg:ml-0 min-h-screen">
        <div className="max-w-7xl mx-auto pt-12 lg:pt-0">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
